/**
 * ThermasolEngine/Debug/DebugRegistry.ts — Vague 17.1
 * ----------------------------------------------------------------
 * Registre CENTRAL des traces Debug. Singleton en mémoire.
 *
 * PRINCIPES :
 *   - Zéro coût quand `enabled = false` (les moteurs appellent
 *     collector.trace(() => {...}) et le callback n'est pas
 *     évalué si off).
 *   - Une session courante (currentSession) capture toutes les
 *     traces émises pendant un run de calcul.
 *   - Les moteurs appellent `registry.beginSession()` avant un
 *     gros calcul multi-moteurs (typiquement l'EngineeringContext
 *     ou un handler UI Step3/4/5), puis `endSession()` à la fin.
 *   - En mode Vague 17.1, on maintient aussi un anneau des N
 *     dernières sessions (`history`, cap 20) pour §7 (historique).
 *
 * PERSISTANCE :
 *   - Par défaut : mémoire uniquement (rapide, zéro I/O)
 *   - Bouton UI "Sauvegarder session Debug" → localStorage
 *     (key `thermasol.debug.saved.<session_id>`).
 */

import type {
  DebugSession,
  FormulaTrace,
  RuleTrace,
  WarningTrace,
  DebugPerfMetrics,
} from "./DebugTypes";
import { nowIso, makeTraceId } from "./DebugTypes";

const HISTORY_CAP = 20;
const LS_ENABLED_KEY = "thermasol.debug.enabled";
const LS_SAVED_PREFIX = "thermasol.debug.saved.";

type SessionListener = (session: DebugSession) => void;

// ─────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────

class Registry {
  private _enabled: boolean;
  private currentSession: DebugSession | null = null;
  private history: DebugSession[] = [];
  private listeners: Set<SessionListener> = new Set();
  private engineStartTimes: Map<string, number> = new Map();
  private engineDurations: Map<string, number> = new Map();

  constructor() {
    // Lecture initiale du flag (localStorage). Silencieux si SSR.
    let stored: string | null = null;
    try {
      stored = typeof window !== "undefined"
        ? window.localStorage.getItem(LS_ENABLED_KEY)
        : null;
    } catch {
      stored = null;
    }
    this._enabled = stored === "true";
  }

  // ── Enabled state ────────────────────────────────────────
  get enabled(): boolean {
    return this._enabled;
  }

  setEnabled(v: boolean): void {
    this._enabled = v;
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LS_ENABLED_KEY, v ? "true" : "false");
        // Notifie les autres tabs
        window.dispatchEvent(new CustomEvent("thermasol-debug-toggle", { detail: { enabled: v } }));
      }
    } catch { /* noop */ }
  }

  // ── Session lifecycle ────────────────────────────────────
  beginSession(projectRef?: string): DebugSession {
    // Ferme silencieusement une session en cours si oubli d'endSession
    if (this.currentSession) {
      this.endSession();
    }
    this.currentSession = {
      session_id: makeTraceId("sess"),
      project_ref: projectRef,
      started_at: nowIso(),
      formulas: [],
      rules: [],
      warnings: [],
      perf: {
        total_duration_ms: 0,
        total_traces: 0,
        total_warnings: 0,
        total_errors: 0,
        per_engine_duration_ms: {},
        per_engine_traces: {},
      },
      self_check_summary: { total: 0, pass: 0, warn: 0, fail: 0 },
    };
    this.engineStartTimes.clear();
    this.engineDurations.clear();
    return this.currentSession;
  }

  endSession(): DebugSession | null {
    if (!this.currentSession) return null;
    const ended: DebugSession = {
      ...this.currentSession,
      ended_at: nowIso(),
      perf: this.buildPerfMetrics(),
    };
    this.history.unshift(ended);
    if (this.history.length > HISTORY_CAP) {
      this.history = this.history.slice(0, HISTORY_CAP);
    }
    this.currentSession = null;
    this.notifyListeners(ended);
    return ended;
  }

  getCurrentSession(): DebugSession | null {
    return this.currentSession;
  }

  getHistory(): DebugSession[] {
    return this.history.slice();
  }

  clearHistory(): void {
    this.history = [];
    this.currentSession = null;
  }

  // ── Trace ingestion (appelée par DebugCollector) ─────────
  pushFormula(trace: FormulaTrace): void {
    const s = this.ensureSession();
    (s.formulas as FormulaTrace[]).push(trace);
    this.updateSelfCheckSummary(trace);
    this.notifyListeners(s);
  }

  pushRule(trace: RuleTrace): void {
    const s = this.ensureSession();
    (s.rules as RuleTrace[]).push(trace);
    this.notifyListeners(s);
  }

  pushWarning(trace: WarningTrace): void {
    const s = this.ensureSession();
    (s.warnings as WarningTrace[]).push(trace);
    if (trace.severity === "FAIL") {
      (s.perf as any).total_errors += 1;
    }
    if (trace.severity === "WARN") {
      (s.perf as any).total_warnings += 1;
    }
    this.notifyListeners(s);
  }

  // ── Perf tracking ────────────────────────────────────────
  markEngineStart(engine: string): void {
    if (!this._enabled) return;
    this.engineStartTimes.set(engine, performance.now());
  }

  markEngineEnd(engine: string): void {
    if (!this._enabled) return;
    const t0 = this.engineStartTimes.get(engine);
    if (t0 == null) return;
    const delta = performance.now() - t0;
    const prev = this.engineDurations.get(engine) || 0;
    this.engineDurations.set(engine, prev + delta);
    this.engineStartTimes.delete(engine);
  }

  // ── Listeners (UI subscribes to re-render) ───────────────
  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ── Save/Export ──────────────────────────────────────────
  saveCurrentToLocalStorage(): string | null {
    const s = this.currentSession || this.history[0];
    if (!s) return null;
    try {
      const key = LS_SAVED_PREFIX + s.session_id;
      window.localStorage.setItem(key, JSON.stringify(s));
      return key;
    } catch { return null; }
  }

  listSavedSessions(): string[] {
    try {
      const out: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(LS_SAVED_PREFIX)) out.push(k);
      }
      return out;
    } catch { return []; }
  }

  loadSavedSession(key: string): DebugSession | null {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as DebugSession) : null;
    } catch { return null; }
  }

  exportJSON(): string {
    const s = this.currentSession || this.history[0] || null;
    return JSON.stringify(s, null, 2);
  }

  exportCSV(): string {
    const s = this.currentSession || this.history[0];
    if (!s) return "";
    const rows: string[] = [];
    rows.push([
      "type", "engine", "engine_version", "id_or_code", "label",
      "severity", "result_or_observed", "unit", "norm", "timestamp",
    ].join(","));
    for (const f of s.formulas) {
      rows.push([
        "formula", f.engine, f.engine_version, f.id, escapeCsv(f.label),
        f.severity, String(f.result), f.result_unit || "",
        (f.norms || []).map((n) => n.code).join(";"),
        f.timestamp,
      ].join(","));
    }
    for (const r of s.rules) {
      rows.push([
        "rule", r.engine, r.engine_version, r.rule_id, escapeCsv(r.label),
        r.status, escapeCsv(r.observed), "", r.norm?.code || "", r.timestamp,
      ].join(","));
    }
    for (const w of s.warnings) {
      rows.push([
        "warning", w.engine, w.engine_version, w.code, escapeCsv(w.title),
        w.severity, escapeCsv(w.message), "",
        (w.norms || []).map((n) => n.code).join(";"),
        w.timestamp,
      ].join(","));
    }
    return rows.join("\n");
  }

  // ── Internals ────────────────────────────────────────────
  private ensureSession(): DebugSession {
    if (!this.currentSession) {
      // Auto-begin session si un moteur émet sans qu'on ait appelé beginSession()
      this.beginSession();
    }
    return this.currentSession!;
  }

  private updateSelfCheckSummary(f: FormulaTrace): void {
    const s = this.currentSession;
    if (!s) return;
    const summary = s.self_check_summary as {
      total: number; pass: number; warn: number; fail: number;
    };
    (s.perf as any).total_traces += 1;
    (s.perf as any).per_engine_traces[f.engine] =
      ((s.perf as any).per_engine_traces[f.engine] || 0) + 1;
    if (f.self_check) {
      summary.total += 1;
      if (f.self_check.ok) summary.pass += 1;
      else if (f.self_check.diff_pct < 5) summary.warn += 1;
      else summary.fail += 1;
    }
  }

  private buildPerfMetrics(): DebugPerfMetrics {
    const s = this.currentSession;
    if (!s) {
      return {
        total_duration_ms: 0, total_traces: 0, total_warnings: 0, total_errors: 0,
        per_engine_duration_ms: {}, per_engine_traces: {},
      };
    }
    const perEngineDurations: Record<string, number> = {};
    let total = 0;
    this.engineDurations.forEach((d, engine) => {
      perEngineDurations[engine] = Math.round(d * 100) / 100;
      total += d;
    });
    return {
      total_duration_ms: Math.round(total * 100) / 100,
      total_traces: (s.perf as any).total_traces || 0,
      total_warnings: (s.perf as any).total_warnings || 0,
      total_errors: (s.perf as any).total_errors || 0,
      per_engine_duration_ms: perEngineDurations,
      per_engine_traces: (s.perf as any).per_engine_traces || {},
    };
  }

  private notifyListeners(s: DebugSession): void {
    this.listeners.forEach((fn) => {
      try { fn(s); } catch { /* noop */ }
    });
  }
}

function escapeCsv(s: string): string {
  if (!s) return "";
  const needsQuotes = /[,"\n]/.test(s);
  const escaped = s.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

// ─────────────────────────────────────────────────────────────
// Singleton exporté
// ─────────────────────────────────────────────────────────────
export const DebugRegistry = new Registry();

// Export du type pour tests éventuels
export type DebugRegistryType = Registry;
